import classNames from 'classnames';
import {
  addDays,
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';
import { fr } from 'date-fns/locale';
import PropTypes from 'prop-types';
import {
  useCallback,
  useMemo,
  useState,
} from 'react';

import { useDisplay } from '../../stores';
import { getLayoutBoundingRect } from '../../utils';

import SelectDropdown from './SelectDropdown';

// Each panel is 7 day-columns plus its month/year selectors
const PANEL_WIDTH = 264;
// Enough room for the header, weekday row and six week rows; used to decide whether to open upward
const POPOVER_HEIGHT = 400;

const capitalizeFirst = (text) => text.charAt(0).toUpperCase() + text.slice(1);

const DateRangeCalendar = function DateRangeCalendar(props) {
  const {
    id,
    mode = 'range',
    value,
    onChange,
    minDate,
    maxDate,
    locale = fr,
    className,
    children,
  } = props;

  const { display } = useDisplay(false);
  const browserHeight = display?.browserHeight ?? globalThis.innerHeight;
  const browserWidth = display?.browserWidth ?? globalThis.innerWidth;
  const weekStartsOn = locale?.options?.weekStartsOn ?? 1;

  // Range mode shows two independently-navigated months, single mode one
  const panelCount = (mode === 'range') ? 2 : 1;
  const popoverWidth = (PANEL_WIDTH * panelCount) + ((panelCount - 1) * 16) + 24;

  const [opened, setOpened] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState({ top: 0, left: 0 });
  // The month shown in each panel; the panels move independently of each other
  const [panelMonths, setPanelMonths] = useState(
    () => Array.from(
      { length: panelCount },
      (unused, index) => startOfMonth(addMonths(new Date(), index - (panelCount - 1))),
    ),
  );
  // First endpoint chosen while a range is being composed (null when idle or single mode)
  const [pendingFirst, setPendingFirst] = useState(null);
  // Day under the cursor, to preview the tentative range before the second click
  const [hoverDay, setHoverDay] = useState(null);

  // The committed selection derived from the value prop
  const selection = useMemo(
    () => {
      if (mode === 'range') {
        if (value?.first && value?.last) {
          return { start: startOfDay(value.first), end: startOfDay(value.last) };
        }
        return null;
      }
      return value ? { start: startOfDay(value), end: startOfDay(value) } : null;
    },
    [mode, value],
  );

  // While composing a range, the highlighted band follows the cursor; otherwise it shows the selection
  const activeRange = useMemo(
    () => {
      if (pendingFirst) {
        const other = hoverDay ?? pendingFirst;
        return isBefore(other, pendingFirst)
          ? { start: startOfDay(other), end: startOfDay(pendingFirst) }
          : { start: startOfDay(pendingFirst), end: startOfDay(other) };
      }
      return selection;
    },
    [pendingFirst, hoverDay, selection],
  );

  const weekdayLabels = useMemo(
    () => {
      const weekStart = startOfWeek(new Date(), { weekStartsOn });
      return Array.from(
        { length: 7 },
        (unused, index) => format(addDays(weekStart, index), 'EEEEEE', { locale }),
      );
    },
    [weekStartsOn, locale],
  );

  const monthChoices = useMemo(
    () => Array.from(
      { length: 12 },
      (unused, index) => ({
        value: String(index),
        text: capitalizeFirst(format(new Date(2021, index, 1), 'LLLL', { locale })),
      }),
    ),
    [locale],
  );

  const yearChoices = useMemo(
    () => {
      const maxYear = (maxDate ?? new Date()).getFullYear();
      const minYear = minDate ? minDate.getFullYear() : (maxYear - 12);
      const count = Math.max(1, (maxYear - minYear) + 1);
      return Array.from(
        { length: count },
        (unused, index) => {
          const year = maxYear - index;
          return { value: String(year), text: String(year) };
        },
      );
    },
    [minDate, maxDate],
  );

  const setPanelMonth = useCallback(
    (index, month) => {
      setPanelMonths((prev) => {
        const next = prev.slice();
        next[index] = startOfMonth(month);
        return next;
      });
    },
    [],
  );

  const isDisabled = useCallback(
    (day) => {
      if (minDate && isBefore(startOfDay(day), startOfDay(minDate))) return true;
      if (maxDate && isAfter(startOfDay(day), startOfDay(maxDate))) return true;
      return false;
    },
    [minDate, maxDate],
  );

  const openPopover = useCallback(
    () => {
      const anchorEl = document.getElementById(id);
      // Layout px, so fixed top/left stay valid under the shell zoom (same trick as SelectDropdown)
      const { left, top, bottom } = getLayoutBoundingRect(anchorEl);

      let leftPos = left;
      if ((leftPos + popoverWidth) > (browserWidth - 8)) leftPos = browserWidth - 8 - popoverWidth;
      if (leftPos < 8) leftPos = 8;

      const style = { left: leftPos };
      if ((top > (browserHeight / 2)) && (top > POPOVER_HEIGHT)) {
        style.bottom = browserHeight - top + 6;
      } else {
        style.top = bottom + 6;
      }
      setPopoverStyle(style);

      if (mode === 'range') {
        const endMonth = startOfMonth(value?.last ?? new Date());
        let startMonth = startOfMonth(value?.first ?? subMonths(endMonth, 1));
        // Keep the two panels on different months so the pair is actually useful
        if (isSameMonth(startMonth, endMonth)) startMonth = subMonths(endMonth, 1);
        setPanelMonths([startMonth, endMonth]);
      } else {
        setPanelMonths([startOfMonth(value ?? new Date())]);
      }

      setPendingFirst(null);
      setHoverDay(null);
      setOpened(true);
    },
    [id, browserWidth, browserHeight, popoverWidth, mode, value],
  );

  const onDayClick = useCallback(
    (day) => {
      if (isDisabled(day)) return;
      const picked = startOfDay(day);

      if (mode === 'single') {
        setOpened(false);
        onChange?.(picked);
        return;
      }

      if (!pendingFirst) {
        setPendingFirst(picked);
        setHoverDay(picked);
        return;
      }

      const [first, last] = isBefore(picked, pendingFirst)
        ? [picked, pendingFirst]
        : [pendingFirst, picked];
      setPendingFirst(null);
      setHoverDay(null);
      setOpened(false);
      onChange?.({ first, last });
    },
    [isDisabled, mode, pendingFirst, onChange],
  );

  const renderPanel = (month, index) => {
    const gridStart = startOfWeek(startOfMonth(month), { weekStartsOn });
    const gridEnd = endOfWeek(endOfMonth(month), { weekStartsOn });
    const days = eachDayOfInterval({ start: gridStart, end: gridEnd });

    return (
      <div key={index} style={{ width: PANEL_WIDTH }}>
        <div className="flex flex-row gap-2 mb-2">
          <SelectDropdown
            id={`${id}-month-${index}`}
            value={String(month.getMonth())}
            choices={monthChoices}
            className="flex-1"
            onChange={(event) => setPanelMonth(
              index,
              new Date(month.getFullYear(), Number.parseInt(event.currentTarget.value, 10), 1),
            )}
          />
          <SelectDropdown
            id={`${id}-year-${index}`}
            value={String(month.getFullYear())}
            choices={yearChoices}
            className="w-[92px]"
            onChange={(event) => setPanelMonth(
              index,
              new Date(Number.parseInt(event.currentTarget.value, 10), month.getMonth(), 1),
            )}
          />
        </div>

        <div className="grid grid-cols-7 mb-1">
          {weekdayLabels.map((label, dayIndex) => (
            <div
              key={dayIndex}
              className="h-7 flex items-center justify-center text-xs text-black/50 capitalize"
            >
              {label}
            </div>
          ))}
        </div>

        <div
          className="grid grid-cols-7"
          onMouseLeave={() => (pendingFirst ? setHoverDay(pendingFirst) : null)}
        >
          {days.map((day) => {
            const disabled = isDisabled(day);
            const outMonth = !isSameMonth(day, month);
            const dayStart = startOfDay(day);

            const isStart = activeRange && isSameDay(dayStart, activeRange.start);
            const isEnd = activeRange && isSameDay(dayStart, activeRange.end);
            const isEndpoint = isStart || isEnd;
            const singleDay = activeRange && isSameDay(activeRange.start, activeRange.end);
            const inRange = activeRange
                         && (!isBefore(dayStart, activeRange.start))
                         && (!isAfter(dayStart, activeRange.end));

            const wrapperCls = classNames(
              'h-9 flex items-center justify-center',
              {
                'bg-pxPrimary/10': inRange && (!singleDay),
                'rounded-l-md': inRange && isStart && (!singleDay),
                'rounded-r-md': inRange && isEnd && (!singleDay),
              },
            );
            const innerCls = classNames(
              'w-9 h-9 flex items-center justify-center text-sm rounded-md transition-colors',
              {
                'bg-pxPrimary text-white font-semibold': isEndpoint,
                'text-black/30 cursor-not-allowed': disabled && (!isEndpoint),
                'text-black/40': outMonth && (!disabled) && (!isEndpoint) && (!inRange),
                'text-black': (!outMonth) && (!disabled) && (!isEndpoint),
                'hover:bg-pxPrimary/20': (!disabled) && (!isEndpoint),
              },
            );

            return (
              <button
                key={day.toISOString()}
                type="button"
                disabled={disabled}
                onClick={() => onDayClick(day)}
                onMouseEnter={() => ((!disabled) && pendingFirst ? setHoverDay(day) : null)}
                className={wrapperCls}
              >
                <span className={innerCls}>{day.getDate()}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <>
      <button
        id={id}
        type="button"
        onClick={openPopover}
        className={classNames('cursor-pointer', className)}
      >
        {children}
      </button>

      {
        opened
          ? (
              <>
                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions,
                 jsx-a11y/click-events-have-key-events */}
                <div
                  className="z-40 fixed inset-0"
                  onClick={() => setOpened(false)}
                />

                <div
                  className="z-40 fixed bg-white rounded-2xl border border-pxMainOutline shadow-card p-3 select-none"
                  style={{ ...popoverStyle, width: popoverWidth }}
                >
                  <div className="flex flex-row gap-4">
                    {panelMonths.map((month, index) => renderPanel(month, index))}
                  </div>
                </div>
              </>
            )
          : null
      }
    </>
  );
};

DateRangeCalendar.propTypes = {
  id: PropTypes.string.isRequired,
  mode: PropTypes.oneOf(['single', 'range']),
  value: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.shape({
      first: PropTypes.instanceOf(Date),
      last: PropTypes.instanceOf(Date),
    }),
  ]),
  onChange: PropTypes.func.isRequired,
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
  locale: PropTypes.shape({ }),
  className: PropTypes.string,
  children: PropTypes.node,
};

export default DateRangeCalendar;
